import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';

interface EmailConfig {
  provider: 'smtp' | 'sendgrid';
  smtp?: {
    host: string;
    port: number;
    user: string;
    password: string;
    secure: boolean;
  };
  sendgrid?: {
    apiKey: string;
  };
  fromName: string;
  fromEmail: string;
  replyTo: string;
}

const EmailSettings: React.FC = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testEmail, setTestEmail] = useState('');

  const [config, setConfig] = useState<EmailConfig>({
    provider: 'smtp',
    smtp: {
      host: 'smtp.gmail.com',
      port: 587,
      user: '',
      password: '',
      secure: false
    },
    sendgrid: {
      apiKey: ''
    },
    fromName: '',
    fromEmail: '',
    replyTo: ''
  });

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/business/email-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.emailConfig) {
          setConfig(data.emailConfig);
        }
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await apiFetch(`${API_URL}/business/email-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emailConfig: config })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Email configuration saved successfully!' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save configuration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter an email address to test' });
      return;
    }

    setTestingEmail(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await apiFetch(`${API_URL}/business/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: testEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: `Test email sent to ${testEmail}!` });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to send test email' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: '#666' }}>Loading email settings...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📧 Email Configuration</h1>
        <p style={styles.subtitle}>
          Configure how your business sends emails to customers
        </p>
      </div>

      {message.text && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          borderColor: message.type === 'success' ? '#c3e6cb' : '#f5c6cb'
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Email Provider</h2>
        <div style={styles.providerButtons}>
          <button
            style={{
              ...styles.providerButton,
              ...(config.provider === 'smtp' ? styles.providerButtonActive : {})
            }}
            onClick={() => setConfig({ ...config, provider: 'smtp' })}
          >
            <div style={styles.providerIcon}>📮</div>
            <div>
              <div style={styles.providerName}>SMTP</div>
              <div style={styles.providerDesc}>Gmail, Outlook, Custom</div>
            </div>
          </button>
          <button
            style={{
              ...styles.providerButton,
              ...(config.provider === 'sendgrid' ? styles.providerButtonActive : {})
            }}
            onClick={() => setConfig({ ...config, provider: 'sendgrid' })}
          >
            <div style={styles.providerIcon}>📨</div>
            <div>
              <div style={styles.providerName}>SendGrid</div>
              <div style={styles.providerDesc}>API-based delivery</div>
            </div>
          </button>
        </div>

        {config.provider === 'smtp' && (
          <div style={styles.section}>
            <h3 style={styles.subSectionTitle}>SMTP Configuration</h3>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>SMTP Host *</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="smtp.gmail.com"
                  value={config.smtp?.host || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    smtp: { ...config.smtp!, host: e.target.value }
                  })}
                />
                <small style={styles.hint}>
                  Gmail: smtp.gmail.com | Outlook: smtp.office365.com
                </small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Port *</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="587"
                  value={config.smtp?.port || 587}
                  onChange={(e) => setConfig({
                    ...config,
                    smtp: { ...config.smtp!, port: parseInt(e.target.value) || 587 }
                  })}
                />
                <small style={styles.hint}>
                  TLS: 587 | SSL: 465
                </small>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username / Email *</label>
                <input
                  type="email"
                  style={styles.input}
                  placeholder="your-business@gmail.com"
                  value={config.smtp?.user || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    smtp: { ...config.smtp!, user: e.target.value }
                  })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Password / App Password *</label>
                <input
                  type="password"
                  style={styles.input}
                  placeholder="••••••••••••••••"
                  value={config.smtp?.password || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    smtp: { ...config.smtp!, password: e.target.value }
                  })}
                />
                <small style={styles.hint}>
                  <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" style={styles.link}>
                    Gmail App Password Guide →
                  </a>
                </small>
              </div>
            </div>

            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                id="secure"
                checked={config.smtp?.secure || false}
                onChange={(e) => setConfig({
                  ...config,
                  smtp: { ...config.smtp!, secure: e.target.checked }
                })}
              />
              <label htmlFor="secure" style={styles.checkboxLabel}>
                Use SSL/TLS (port 465)
              </label>
            </div>
          </div>
        )}

        {config.provider === 'sendgrid' && (
          <div style={styles.section}>
            <h3 style={styles.subSectionTitle}>SendGrid Configuration</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>SendGrid API Key *</label>
              <input
                type="password"
                style={styles.input}
                placeholder="SG.xxxxxxxxxxxxxxxxxx"
                value={config.sendgrid?.apiKey || ''}
                onChange={(e) => setConfig({
                  ...config,
                  sendgrid: { apiKey: e.target.value }
                })}
              />
              <small style={styles.hint}>
                <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" style={styles.link}>
                  Get your API key from SendGrid →
                </a>
              </small>
            </div>
          </div>
        )}

        <div style={styles.section}>
          <h3 style={styles.subSectionTitle}>Email Identity</h3>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>From Name *</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Your Business Name"
                value={config.fromName}
                onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
              />
              <small style={styles.hint}>
                This name appears in customer's inbox
              </small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>From Email *</label>
              <input
                type="email"
                style={styles.input}
                placeholder="noreply@yourbusiness.com"
                value={config.fromEmail}
                onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Reply-To Email</label>
            <input
              type="email"
              style={styles.input}
              placeholder="support@yourbusiness.com"
              value={config.replyTo}
              onChange={(e) => setConfig({ ...config, replyTo: e.target.value })}
            />
            <small style={styles.hint}>
              Where customer replies will be sent (optional)
            </small>
          </div>
        </div>

        <div style={styles.actions}>
          <button
            style={{
              ...styles.button,
              ...styles.saveButton,
              opacity: saving ? 0.6 : 1
            }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '💾 Saving...' : '💾 Save Configuration'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Test Email Configuration</h2>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Send a test email to verify your configuration works correctly
        </p>

        <div style={styles.formGroup}>
          <label style={styles.label}>Test Email Address</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="email"
              style={{ ...styles.input, flex: 1 }}
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <button
              style={{
                ...styles.button,
                ...styles.testButton,
                opacity: testingEmail ? 0.6 : 1
              }}
              onClick={handleTestEmail}
              disabled={testingEmail}
            >
              {testingEmail ? '📤 Sending...' : '📤 Send Test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    margin: 0
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid',
    fontSize: '14px'
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '20px'
  },
  subSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
    marginTop: '20px'
  },
  section: {
    marginTop: '25px'
  },
  providerButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '20px'
  },
  providerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '20px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left'
  },
  providerButtonActive: {
    borderColor: '#4f46e5',
    backgroundColor: '#f5f3ff'
  },
  providerIcon: {
    fontSize: '32px'
  },
  providerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a1a1a'
  },
  providerDesc: {
    fontSize: '13px',
    color: '#666',
    marginTop: '2px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '15px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  },
  hint: {
    display: 'block',
    fontSize: '12px',
    color: '#888',
    marginTop: '4px'
  },
  link: {
    color: '#4f46e5',
    textDecoration: 'none'
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '15px'
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer'
  },
  actions: {
    marginTop: '30px',
    display: 'flex',
    gap: '10px'
  },
  button: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  saveButton: {
    backgroundColor: '#4f46e5',
    color: '#fff'
  },
  testButton: {
    backgroundColor: '#10b981',
    color: '#fff',
    whiteSpace: 'nowrap'
  }
};

export default EmailSettings;
